import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import CBMediaQuery from '../../shared/CBMediaQuery';
import { getStyle, formatDate } from '../../../utils/utils';
import CBSpinnerFloating from '../../shared/CBSpinnerFloating';
import CBPromptModal from '../../shared/CBPromptModal';
import { Button } from 'primereact/button';
import InviteToChainCareServiceModal from './InviteToChainCareServiceModal';
import {
  deleteChainCareService as _deleteChainCareService,
  updateChainCareService as _updateChainCareService,
  fetchChainInvoiceSettings as _fetchChainInvoiceSettings,
} from '../../../actions/chainActions';
import ChainCustomerMappingsV2 from './ChainCustomerMappingsV2';
import CareServiceInvoicingSettings, { careServiceRhythmLine } from './CareServiceInvoicingSettings';
import { T, cardStyle, statCardStyle, tagStyle } from './chainSettingsV2Styles';

class SettingsChainCareServicesV2 extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showInviteModal: false,
      expandedCareServiceId: null,
      deleteModalOpen: false,
      deleteTargetId: null,
      csForm: {},
      csFormDirty: false,
    };
  }

  componentDidMount() {
    // The per-customer payment term shows the chain default as its placeholder,
    // so the settings must be loaded even when this tab is opened first.
    if (!this.props.chainInvoiceSettings && this.props.chainId) {
      this.props.fetchChainInvoiceSettings(this.props.chainId);
    }
  }

  openDeleteModal = (careServiceId) => {
    this.setState({ deleteModalOpen: true, deleteTargetId: careServiceId });
  };

  closeDeleteModal = () => {
    this.setState({ deleteModalOpen: false, deleteTargetId: null });
  };

  confirmDelete = () => {
    var deleteChainCareService = this.props.deleteChainCareService;
    var deleteTargetId = this.state.deleteTargetId;
    if (deleteTargetId) {
      deleteChainCareService(deleteTargetId);
    }
    this.closeDeleteModal();
  };

  initCsForm = function (cs) {
    this.setState({
      csForm: {
        assigned_workshop_id: cs.assigned_workshop || '',
        netvisor_customer_number: cs.netvisor_customer_number || '',
        invoicing_interval: cs.invoicing_interval || 'monthly',
        invoice_detail_level: cs.invoice_detail_level || 'summary',
        work_hourly_surcharge_override: cs.work_hourly_surcharge_override != null ? String(cs.work_hourly_surcharge_override) : '',
        parts_markup_percentage_override: cs.parts_markup_percentage_override != null ? String(cs.parts_markup_percentage_override) : '',
        invoice_per_purchase_invoice: cs.invoice_per_purchase_invoice || false,
        invoicing_anchor_day: cs.invoicing_anchor_day || 1,
        invoice_on_month_end: cs.invoice_on_month_end || false,
        invoicing_paused_until: cs.invoicing_paused_until || '',
        outstanding_policy: cs.outstanding_policy || 'sweep_all',
        requires_approval: cs.requires_approval || false,
        export_mode: cs.export_mode || 'manual',
        export_interval: cs.export_interval || 'weekly',
        export_anchor_day: cs.export_anchor_day || 1,
        payment_term_days_override: cs.payment_term_days_override != null ? String(cs.payment_term_days_override) : '',
      },
      csFormDirty: false,
    });
  }.bind(this);

  updateCsForm = function (field, value) {
    this.setState({
      csForm: { ...this.state.csForm, [field]: value },
      csFormDirty: true,
    });
  }.bind(this);

  // '' / null -> null (= use chain default); '1,5' -> 1.5; garbage -> undefined.
  normalizeMarginValue = function (value) {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    var normalized = String(value).replace(',', '.').trim();
    if (!/^\d+(\.\d+)?$/.test(normalized)) {
      return undefined;
    }
    return normalized;
  };

  // '' -> null (= use the chain default); '21' -> 21; anything else -> undefined.
  normalizePaymentTerm = function (value) {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    var normalized = String(value).trim();
    if (!/^\d{1,3}$/.test(normalized)) {
      return undefined;
    }
    return Number(normalized);
  };

  saveCsForm = function (careServiceId) {
    var form = this.state.csForm;
    var work = this.normalizeMarginValue(form.work_hourly_surcharge_override);
    var parts = this.normalizeMarginValue(form.parts_markup_percentage_override);
    var paymentTerm = this.normalizePaymentTerm(form.payment_term_days_override);
    if (work === undefined || parts === undefined || paymentTerm === undefined) {
      this.setState({ csFormError: this.props.t('invalidDecimalValue') });
      return;
    }
    this.props.updateChainCareService(careServiceId, {
      assigned_workshop_id: form.assigned_workshop_id === '' ? null : form.assigned_workshop_id,
      netvisor_customer_number: form.netvisor_customer_number,
      invoicing_interval: form.invoicing_interval,
      invoice_detail_level: form.invoice_detail_level,
      work_hourly_surcharge_override: work,
      parts_markup_percentage_override: parts,
      invoice_per_purchase_invoice: form.invoice_per_purchase_invoice,
      invoicing_anchor_day: Number(form.invoicing_anchor_day) || 1,
      invoice_on_month_end: form.invoice_on_month_end,
      invoicing_paused_until: form.invoicing_paused_until || null,
      outstanding_policy: form.outstanding_policy,
      requires_approval: form.requires_approval,
      export_mode: form.export_mode,
      export_interval: form.export_interval,
      export_anchor_day: Number(form.export_anchor_day) || 1,
      payment_term_days_override: paymentTerm,
      // Legacy flag, kept in step with export_mode so support tooling and the
      // admin never see the two disagree.
      auto_export_to_netvisor: form.export_mode !== 'manual',
    });
    this.setState({ csFormDirty: false, csFormError: null });
  }.bind(this);

  expandCareService = function (careServiceId, careService) {
    var isExpanded = this.state.expandedCareServiceId === careServiceId;
    if (isExpanded) {
      this.setState({ expandedCareServiceId: null, csForm: {}, csFormDirty: false });
    } else {
      this.setState({ expandedCareServiceId: careServiceId });
      this.initCsForm(careService);
    }
  }.bind(this);

  render() {
    var t = this.props.t;
    var chainId = this.props.chainId;
    var chainCareServices = this.props.chainCareServices;
    var careServicesStatus = this.props.careServicesStatus;
    var chainCareServiceInvitations = this.props.chainCareServiceInvitations;
    var showInviteModal = this.state.showInviteModal;
    var deleteModalOpen = this.state.deleteModalOpen;
    var csForm = this.state.csForm;
    var csFormDirty = this.state.csFormDirty;
    var csFormError = this.state.csFormError;
    var blockExternalWrites = this.props.blockExternalWrites;

    var isLoading = careServicesStatus && careServicesStatus.loading;

    var pendingInvitations = chainCareServiceInvitations.filter(function (inv) {
      return inv.status === 'pending';
    });

    var activeCount = chainCareServices.filter(function (cs) {
      return cs.is_active;
    }).length;

    var totalVehicles = chainCareServices.reduce(function (sum, cs) {
      return sum + (cs.vehicle_count || 0);
    }, 0);

    return (
      <CBMediaQuery>
        {screenSize => (
          <div>
            <div style={getStyle(screenSize, styles, 'headerCard')}>
              <div style={getStyle(screenSize, styles, 'headerLeft')}>
                <div style={getStyle(screenSize, styles, 'iconBox')}>
                  <i className="pi pi-heart" style={{ fontSize: 20, color: T.accent }} />
                </div>
                <div>
                  <h2 style={getStyle(screenSize, styles, 'title')}>{t('chainCareServicesTitle')}</h2>
                  <p style={getStyle(screenSize, styles, 'description')}>{t('chainCareServicesDescription')}</p>
                </div>
              </div>
              <Button
                onClick={() => this.setState({ showInviteModal: true })}
                label={t('inviteOrganization')}
                style={{
                  backgroundColor: T.accent,
                  border: 'none',
                  color: '#0077b0',
                  fontWeight: 600,
                  fontSize: 13,
                }}
              />
            </div>

            <div style={getStyle(screenSize, styles, 'statsRow')}>
              <div style={statCardStyle}>
                <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  {t('activeContracts')}
                </div>
                <div style={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>
                  {activeCount}
                </div>
              </div>
              <div style={statCardStyle}>
                <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  {t('totalVehicles')}
                </div>
                <div style={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>
                  {totalVehicles} <span style={{ fontSize: 14, fontWeight: 400, color: T.textMuted }}>{t('vehiclesCount')}</span>
                </div>
              </div>
              <div style={statCardStyle}>
                <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  {t('pendingInvitationsCount')}
                </div>
                <div style={{ color: pendingInvitations.length > 0 ? '#fff' : T.textMuted, fontSize: 28, fontWeight: 700 }}>
                  {pendingInvitations.length}
                </div>
              </div>
            </div>

            {isLoading && (
              <div style={{ ...cardStyle, position: 'relative', minHeight: 100 }}>
                <CBSpinnerFloating color={T.accent} />
              </div>
            )}

            {!isLoading && (
              <div style={getStyle(screenSize, styles, 'tableCard')}>
                {chainCareServices.length === 0 && pendingInvitations.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px 24px' }}>
                    <i className="pi pi-heart" style={{ fontSize: 32, color: T.textMuted, marginBottom: 12, display: 'block' }} />
                    <p style={{ color: T.textMuted, fontSize: 14, margin: 0 }}>
                      {t('noCareServices')}
                    </p>
                  </div>
                )}

                {screenSize === 'large' && (chainCareServices.length > 0 || pendingInvitations.length > 0) && (
                  <div style={getStyle(screenSize, styles, 'tableHeader')}>
                    <span style={{ ...styles.default.colOrg, ...styles.default.headerCell }}>{t('clientCompany')}</span>
                    <span style={{ ...styles.default.colVehicles, ...styles.default.headerCell }}>{t('vehicles')}</span>
                    <span style={{ ...styles.default.colStatus, ...styles.default.headerCell }}>{t('status')}</span>
                    <span style={{ ...styles.default.colDate, ...styles.default.headerCell }}>{t('started')}</span>
                    <span style={styles.default.colActions} />
                  </div>
                )}

                {screenSize === 'large' && pendingInvitations.map(function (invitation) {
                  return (
                    <div key={'inv-' + invitation.id} style={getStyle(screenSize, styles, 'row')}>
                      <div style={styles.default.colOrg}>
                        <div style={getStyle(screenSize, styles, 'orgCell')}>
                          <div style={{
                            ...styles.default.orgIconBox,
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          }}>
                            <i className="pi pi-envelope" style={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)' }} />
                          </div>
                          <span style={{ color: T.textPrimary, fontSize: 14, fontWeight: 500 }}>
                            {invitation.organization_name || invitation.email}
                          </span>
                        </div>
                      </div>
                      <div style={styles.default.colVehicles}>
                        <span style={{ color: T.textMuted, fontSize: 13 }}>-</span>
                      </div>
                      <div style={styles.default.colStatus}>
                        <span style={tagStyle('pending')}>
                          {t('pending')}
                        </span>
                      </div>
                      <div style={styles.default.colDate}>
                        <span style={{ color: T.textSecondary, fontSize: 13 }}>
                          {invitation.created_at ? formatDate(invitation.created_at) : '-'}
                        </span>
                      </div>
                      <div style={styles.default.colActions} />
                    </div>
                  );
                })}

                {screenSize === 'large' && chainCareServices.map(function (careService) {
                  var orgName = careService.organization_name || ('Yritys #' + careService.organization);
                  var isExpanded = this.state.expandedCareServiceId === careService.id;

                  return (
                    <div key={careService.id}>
                      <div style={getStyle(screenSize, styles, 'row')}>
                        <div style={styles.default.colOrg}>
                          <div style={getStyle(screenSize, styles, 'orgCell')}>
                            <div style={{
                              ...styles.default.orgIconBox,
                              backgroundColor: careService.is_active ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255,255,255,0.05)',
                            }}>
                              <i className="pi pi-building" style={{
                                fontSize: 16,
                                color: careService.is_active ? '#fff' : T.textMuted,
                              }} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ color: T.textPrimary, fontSize: 14, fontWeight: 500 }}>
                                {orgName}
                              </span>
                              <span style={{ color: T.textMuted, fontSize: 11 }}>
                                {careServiceRhythmLine(careService, t)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div style={styles.default.colVehicles}>
                          <span style={{ color: T.textSecondary, fontSize: 14, fontWeight: 600 }}>
                            {careService.vehicle_count || 0}
                          </span>
                        </div>
                        <div style={styles.default.colStatus}>
                          {careService.is_active ? (
                            <span style={tagStyle('active')}>
                              {t('active')}
                            </span>
                          ) : (
                            <span style={tagStyle('pending')}>
                              {t('pending')}
                            </span>
                          )}
                        </div>
                        <div style={styles.default.colDate}>
                          <span style={{ color: T.textSecondary, fontSize: 13 }}>
                            {careService.created_at ? formatDate(careService.created_at) : '-'}
                          </span>
                        </div>
                        <div style={styles.default.colActions}>
                          <Button
                            onClick={function () {
                              this.expandCareService(careService.id, careService);
                            }.bind(this)}
                            icon={isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                            text
                            rounded
                            style={{ color: T.textMuted, width: 28, height: 28 }}
                          />
                        </div>
                      </div>
                      {isExpanded && (
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid ' + T.borderLight, borderTop: '1px solid ' + T.borderLight }}>
                          <CareServiceInvoicingSettings
                            careService={careService}
                            csForm={csForm}
                            updateCsForm={this.updateCsForm}
                            chainMemberships={this.props.chainMemberships}
                            chainPaymentTerm={this.props.chainPaymentTerm}
                            blockExternalWrites={blockExternalWrites}
                            csFormError={csFormError}
                          />

                          {csFormError && (
                            <p style={{ color: '#EF9A9A', fontSize: 12, margin: '4px 0' }}>{csFormError}</p>
                          )}
                          <div style={{ marginTop: 12 }}>
                            <Button
                              onClick={function () { this.saveCsForm(careService.id); }.bind(this)}
                              label={t('saveChanges')}
                              disabled={!csFormDirty}
                              style={{
                                backgroundColor: csFormDirty ? T.accent : 'rgba(255,255,255,0.1)',
                                border: 'none',
                                color: csFormDirty ? '#0077b0' : T.textMuted,
                                fontWeight: 600,
                                fontSize: 12,
                                opacity: csFormDirty ? 1 : 0.5,
                              }}
                            />
                          </div>

                          <ChainCustomerMappingsV2 careServiceId={careService.id} />

                          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                            <Button
                              onClick={() => this.openDeleteModal(careService.id)}
                              label={t('terminate')}
                              size="small"
                              outlined
                              style={{
                                borderColor: 'rgba(255, 80, 80, 0.5)',
                                color: 'rgba(255, 80, 80, 0.8)',
                                fontSize: 12,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }.bind(this))}

                {screenSize !== 'large' && pendingInvitations.map(function (invitation) {
                  return (
                    <div key={'inv-' + invitation.id} style={getStyle(screenSize, styles, 'mobileCard')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <div style={{ ...styles.default.orgIconBox, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
                          <i className="pi pi-envelope" style={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)' }} />
                        </div>
                        <span style={{ color: T.textPrimary, fontSize: 14, fontWeight: 500 }}>
                          {invitation.organization_name || invitation.email}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={tagStyle('pending')}>{t('pending')}</span>
                        <span style={{ color: T.textSecondary, fontSize: 12 }}>
                          {invitation.created_at ? formatDate(invitation.created_at) : '-'}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {screenSize !== 'large' && chainCareServices.map(function (careService) {
                  var orgName = careService.organization_name || ('Yritys #' + careService.organization);
                  var isMobileExpanded = this.state.expandedCareServiceId === careService.id;

                  return (
                    <div key={careService.id} style={getStyle(screenSize, styles, 'mobileCard')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            ...styles.default.orgIconBox,
                            backgroundColor: careService.is_active ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255,255,255,0.05)',
                          }}>
                            <i className="pi pi-building" style={{
                              fontSize: 16,
                              color: careService.is_active ? '#fff' : T.textMuted,
                            }} />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ color: T.textPrimary, fontSize: 14, fontWeight: 500 }}>
                              {orgName}
                            </span>
                            <span style={{ color: T.textMuted, fontSize: 11 }}>
                              {careServiceRhythmLine(careService, t)}
                            </span>
                          </div>
                        </div>
                        <Button
                          onClick={function () {
                            this.expandCareService(careService.id, careService);
                          }.bind(this)}
                          icon={isMobileExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                          text
                          rounded
                          style={{ color: T.textMuted, width: 28, height: 28 }}
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ color: T.textSecondary, fontSize: 13 }}>
                          {(careService.vehicle_count || 0) + ' ' + t('vehiclesCount')}
                        </span>
                        {careService.is_active ? (
                          <span style={tagStyle('active')}>{t('active')}</span>
                        ) : (
                          <span style={tagStyle('pending')}>{t('pending')}</span>
                        )}
                        <span style={{ color: T.textSecondary, fontSize: 12 }}>
                          {careService.created_at ? formatDate(careService.created_at) : '-'}
                        </span>
                      </div>
                      {isMobileExpanded && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid ' + T.borderLight }}>
                          <CareServiceInvoicingSettings
                            careService={careService}
                            csForm={csForm}
                            updateCsForm={this.updateCsForm}
                            chainMemberships={this.props.chainMemberships}
                            chainPaymentTerm={this.props.chainPaymentTerm}
                            blockExternalWrites={blockExternalWrites}
                            csFormError={csFormError}
                          />
                          {csFormError && (
                            <p style={{ color: '#EF9A9A', fontSize: 12, margin: '4px 0' }}>{csFormError}</p>
                          )}
                          <div style={{ marginTop: 12 }}>
                            <Button
                              onClick={function () { this.saveCsForm(careService.id); }.bind(this)}
                              label={t('saveChanges')}
                              disabled={!csFormDirty}
                              style={{
                                backgroundColor: csFormDirty ? T.accent : 'rgba(255,255,255,0.1)',
                                border: 'none',
                                color: csFormDirty ? '#0077b0' : T.textMuted,
                                fontWeight: 600,
                                fontSize: 12,
                                opacity: csFormDirty ? 1 : 0.5,
                              }}
                            />
                          </div>
                          <ChainCustomerMappingsV2 careServiceId={careService.id} />
                          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                            <Button
                              onClick={() => this.openDeleteModal(careService.id)}
                              label={t('terminate')}
                              size="small"
                              outlined
                              style={{
                                borderColor: 'rgba(255, 80, 80, 0.5)',
                                color: 'rgba(255, 80, 80, 0.8)',
                                fontSize: 12,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }.bind(this))}
              </div>
            )}

            {showInviteModal && (
              <InviteToChainCareServiceModal
                modalIsOpen={showInviteModal}
                selectedChainId={chainId}
                closeModal={() => this.setState({ showInviteModal: false })}
              />
            )}

            <CBPromptModal
              modalIsOpen={deleteModalOpen}
              closeModal={this.closeDeleteModal}
              performAction={this.confirmDelete}
              title={t('terminateCareServiceTitle')}
              text={t('terminateCareServiceText')}
              buttonText={t('terminateCareServiceButton')}
            />
          </div>
        )}
      </CBMediaQuery>
    );
  }
}

var styles = {
  default: {
    headerCard: {
      ...cardStyle,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: 12,
      marginBottom: 20,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
    },
    iconBox: {
      width: 48,
      height: 48,
      borderRadius: 12,
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    title: {
      color: T.textPrimary,
      fontSize: 18,
      fontWeight: 600,
      margin: 0,
    },
    description: {
      color: T.textSecondary,
      fontSize: 13,
      margin: '4px 0 0 0',
    },
    statsRow: {
      display: 'flex',
      gap: 16,
      marginBottom: 20,
      flexWrap: 'wrap',
    },
    tableCard: {
      ...cardStyle,
    },
    tableHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 16px',
      borderBottom: '1px solid ' + T.border,
      marginBottom: 4,
    },
    headerCell: {
      color: T.textMuted,
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      padding: '14px 16px',
      borderBottom: '1px solid ' + T.borderLight,
    },
    colOrg: {
      flex: 3,
      minWidth: 0,
    },
    colVehicles: {
      flex: 1,
      minWidth: 60,
      textAlign: 'center',
    },
    colStatus: {
      flex: 1.2,
      minWidth: 80,
    },
    colDate: {
      flex: 1.2,
      minWidth: 80,
    },
    colActions: {
      width: 90,
      display: 'flex',
      justifyContent: 'flex-end',
    },
    orgCell: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    },
    orgIconBox: {
      width: 36,
      height: 36,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
  },
  medium: {
    headerCard: {
      ...cardStyle,
      padding: 16,
      marginBottom: 12,
    },
    statsRow: {
      gap: 10,
      marginBottom: 12,
    },
    tableCard: {
      ...cardStyle,
      padding: 12,
    },
    mobileCard: {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      borderRadius: 10,
      padding: 14,
      marginBottom: 8,
      border: '1px solid ' + T.borderLight,
    },
  },
};

function mapStateToProps(state) {
  var selectedChainId = state.chain.selectedChainId;
  var user = state.authUser.user;
  var userChains = user && user.chains ? user.chains : [];
  var currentChain = userChains.find(function (c) { return c.id === selectedChainId; });
  return {
    chainCareServices: state.chain.chainCareServices,
    careServicesStatus: state.chain.chainCareServicesStatus,
    chainCareServiceInvitations: state.chain.chainCareServiceInvitations,
    chainMemberships: state.chain.chainMemberships,
    blockExternalWrites: currentChain ? !!currentChain.block_external_writes : false,
    chainInvoiceSettings: state.chain.chainInvoiceSettings,
    chainPaymentTerm: state.chain.chainInvoiceSettings
      && state.chain.chainInvoiceSettings.default_payment_term_days
      ? state.chain.chainInvoiceSettings.default_payment_term_days : 14,
  };
}

export default connect(
  mapStateToProps,
  {
    deleteChainCareService: _deleteChainCareService,
    updateChainCareService: _updateChainCareService,
    fetchChainInvoiceSettings: _fetchChainInvoiceSettings,
  },
)(translate('ChainSettingsView')(SettingsChainCareServicesV2));
